import { z } from 'zod';
import { NOTIFICATION_TYPES, USER_STATUSES } from '@/lib/shared-types/constants';

// Common validation patterns
const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
const phonePattern = /^\+?[\d\s\-\(\)]{10,}$/;
const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

// User authentication schemas
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(254, 'Email is too long'),

  password: z
    .string()
    .min(1, 'Password is required')
    .max(128, 'Password is too long'),
});

export const registerSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name contains invalid characters'),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(254, 'Email is too long'),

  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password is too long')
    .regex(passwordPattern, 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'),

  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export const requestAccessSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name contains invalid characters'),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(254, 'Email is too long'),

  phone: z
    .string()
    .optional()
    .refine((val) => !val || phonePattern.test(val), {
      message: 'Invalid phone number format',
    }),

  role: z
    .enum(['member', 'media_team', 'pastor'])
    .catch(() => 'member' as const),

  message: z
    .string()
    .max(500, 'Message is too long')
    .optional(),
});

// Stream management schemas
export const createStreamSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title is too long')
    .regex(/^[^<>&"']+$/, 'Title contains invalid characters'),

  description: z
    .string()
    .max(2000, 'Description is too long')
    .optional(),

  scheduledStart: z
    .string()
    .min(1, 'Scheduled start time is required')
    .refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid date format',
    })
    .refine((val) => new Date(val) > new Date(), {
      message: 'Scheduled time must be in the future',
    }),

  thumbnailUrl: z
    .string()
    .url('Invalid thumbnail URL')
    .optional()
    .or(z.literal('')),

  featureImageUrl: z
    .string()
    .url('Invalid feature image URL')
    .optional()
    .or(z.literal('')),

  inviteRequired: z.boolean().optional().default(false),

  visibility: z.enum(['public', 'members', 'invite_only']).optional(),

  segmentId: z.number().int().positive().optional(),

  regionRestriction: z
    .string()
    .optional(),

  tags: z
    .union([z.string(), z.array(z.string())])
    .optional(),
}).refine((data) => {
  if (data.visibility === 'invite_only') {
    return typeof data.segmentId === 'number' && data.segmentId > 0;
  }
  return true;
}, {
  message: 'segmentId is required for invite_only streams',
  path: ['segmentId'],
});

export const updateStreamSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title is too long')
    .regex(/^[^<>&"']+$/, 'Title contains invalid characters')
    .optional(),

  description: z
    .string()
    .max(2000, 'Description is too long')
    .optional(),

  scheduledStart: z
    .string()
    .refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid date format',
    })
    .optional(),

  thumbnailUrl: z
    .string()
    .url('Invalid thumbnail URL')
    .optional()
    .or(z.literal('')),

  featureImageUrl: z
    .string()
    .url('Invalid feature image URL')
    .optional()
    .or(z.literal('')),

  inviteRequired: z.boolean().optional(),

  visibility: z.enum(['public', 'members', 'invite_only']).optional(),

  segmentId: z.number().int().positive().optional(),

  regionRestriction: z
    .string()
    .optional(),

  tags: z
    .union([z.string(), z.array(z.string())])
    .optional(),

  status: z.enum(['scheduled', 'live', 'ended']).optional(),
});

// Comment schemas
export const createCommentSchema = z.object({
  content: z
    .string()
    .min(1, 'Comment cannot be empty')
    .max(1000, 'Comment is too long')
    .refine((val) => val.trim().length > 0, 'Comment cannot be only whitespace')
    .refine((val) => !/<script|javascript:|on\w+=/i.test(val), {
      message: 'Comment contains invalid content',
    }),

  parentId: z
    .number()
    .int()
    .positive('Invalid parent comment ID')
    .optional(),
});

export const reportCommentSchema = z.object({
  reason: z.enum(
    ['spam', 'harassment', 'inappropriate', 'off_topic', 'other']
  ),

  description: z
    .string()
    .max(500, 'Description is too long')
    .optional(),
});

// User management schemas (admin only)
export const createUserSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name contains invalid characters'),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(254, 'Email is too long'),

  role: z.enum(['member', 'media_team', 'pastor', 'admin']),

  status: z.enum(USER_STATUSES),

  phone: z
    .string()
    .optional()
    .refine((val) => !val || phonePattern.test(val), {
      message: 'Invalid phone number format',
    }),

  profilePictureUrl: z.string().optional(),
});

export const adminCreateUserSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name contains invalid characters'),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(254, 'Email is too long'),

  roleName: z.enum(['member', 'media_team', 'pastor', 'admin', 'moderator']),
});

export const updateUserSchema = createUserSchema.partial().extend({
  id: z.number().int().positive('Invalid user ID'),
});

// Notification schemas
export const createNotificationSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title is too long'),

  message: z
    .string()
    .min(1, 'Message is required')
    .max(1000, 'Message is too long'),

  type: z.enum(NOTIFICATION_TYPES),

  recipientIds: z
    .array(z.number().int().positive())
    .min(1, 'At least one recipient is required'),

  actionUrl: z
    .string()
    .url('Invalid action URL')
    .optional(),

  actionLabel: z
    .string()
    .max(50, 'Action label is too long')
    .optional(),


  expiresAt: z
    .string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), {
      message: 'Invalid expiration date format',
    }),
});

// Settings schemas
export const updateSettingsSchema = z.object({
  // Notification preferences
  emailNotifications: z.boolean(),
  pushNotifications: z.boolean(),
  liveStreamAlerts: z.boolean(),
  commentReplies: z.boolean(),
  newFollowers: z.boolean(),

  // Privacy settings
  profileVisibility: z.enum(['public', 'members', 'private']),
  showOnlineStatus: z.boolean(),
  allowMessages: z.boolean(),

  // Streaming preferences
  autoPlay: z.boolean(),
  defaultQuality: z.enum(['auto', '360p', '480p', '720p', '1080p']),
  showCaptions: z.boolean(),
  reduceMotion: z.boolean(),

  // Appearance
  theme: z.enum(['light', 'dark', 'system']),
  language: z.enum(['en', 'es', 'fr', 'de']),
  timezone: z.enum(['auto', 'utc', 'est', 'pst', 'gmt']),
});

// Search and filter schemas
export const searchStreamsSchema = z.object({
  query: z
    .string()
    .max(100, 'Search query is too long')
    .optional(),

  status: z
    .enum(['live', 'scheduled', 'ended'])
    .optional(),

  creator: z
    .string()
    .max(100, 'Creator name is too long')
    .optional(),

  tags: z
    .array(z.string().max(50))
    .max(5, 'Too many tags')
    .optional(),

  dateFrom: z
    .string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), {
      message: 'Invalid date format',
    }),

  dateTo: z
    .string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), {
      message: 'Invalid date format',
    }),

  sortBy: z
    .enum(['createdAt', 'scheduledStart', 'viewerCount', 'title'])
    .default('createdAt'),

  sortOrder: z
    .enum(['asc', 'desc'])
    .default('desc'),

  page: z
    .number()
    .int()
    .min(1)
    .default(1),

  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .default(20),
});

// API response validation schemas
export const apiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
    message: z.string().optional(),
    errors: z.array(z.object({
      field: z.string(),
      message: z.string(),
    })).optional(),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
      hasNext: z.boolean(),
      hasPrev: z.boolean(),
    }).optional(),
  });

export const errorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  message: z.string().optional(),
  code: z.string().optional(),
  details: z.any().optional(),
});

// Utility functions for validation
export const validateData = <T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: z.ZodError } => {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, errors: result.error };
  }
};

export const formatValidationErrors = (error: z.ZodError): string[] => {
  return error.issues.map((err: any) => {
    const path = err.path.join('.');
    return path ? `${path}: ${err.message}` : err.message;
  });
};

// Type exports for use in components
export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type RequestAccessInput = z.infer<typeof requestAccessSchema>;
export type CreateStreamInput = z.infer<typeof createStreamSchema>;
export type UpdateStreamInput = z.infer<typeof updateStreamSchema>;
export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type ReportCommentInput = z.infer<typeof reportCommentSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type CreateNotificationInput = z.infer<typeof createNotificationSchema>;
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;
export type SearchStreamsInput = z.infer<typeof searchStreamsSchema>;
